import { TransitionPass } from './passes/TransitionPass.js';
import { AutotilePass } from './passes/AutotilePass.js';
import { CleanupPass } from './passes/CleanupPass.js';
import { DecorationPass } from './passes/DecorationPass.js';
import { ValidationPass } from './passes/ValidationPass.js';
import { QualityScoringPass } from './passes/QualityScoringPass.js';

/**
 * Multi-pass map generation pipeline.
 *
 * Orchestrates the flow:
 *   Generator (structure) → Zone Map
 *     → WFC Tile Resolver (optional, High quality)
 *     → Transition Pass
 *     → Cleanup Pass
 *     → Decoration Pass
 *     → Validation Pass
 *     → Final grids
 *
 * Each pass is a named entry with a `process` method and its own config.
 * Passes can be added, removed, or reordered before running.
 */
export class MapPipeline {
  /**
   * @param {import('./SeededRandom.js').SeededRandom} rng
   */
  constructor(rng) {
    this.rng = rng;
    /** @type {Array<{name: string, pass: object, config: object}>} */
    this._passes = [];
  }

  /**
   * Append a pass to the pipeline.
   * @param {string} name
   * @param {object} pass — must have a `process()` method
   * @param {object} [config] — pass-specific config forwarded to process()
   */
  addPass(name, pass, config = {}) {
    this._passes.push({ name, pass, config });
    return this;
  }

  /**
   * Remove a pass by name.
   */
  removePass(name) {
    this._passes = this._passes.filter(p => p.name !== name);
    return this;
  }

  /**
   * Get a pass entry by name (for reconfiguring).
   */
  getPass(name) {
    return this._passes.find(p => p.name === name) || null;
  }

  /**
   * Run the full pipeline.
   *
   * @param {object} structureResult — output from generator's structure pass:
   *   { ground, objects, overhead, zones, width, height, metadata }
   *   - ground/objects/overhead: 2D arrays of tile/zone IDs
   *   - zones: optional 2D array of ZONE constants (used by WFC)
   *   - metadata: generator-specific data (rooms, clearings, etc.)
   * @param {object} options — per-run overrides
   *   - tileMapping: zone → tile mapping for TransitionPass
   *   - floorTileIds: walkable tile IDs for ValidationPass
   *   - corridorTileId: repair corridor tile
   *   - decorations: decoration specs for DecorationPass
   * @returns {object} { ground, objects, overhead, collision, width, height, metadata }
   */
  run(structureResult, options = {}) {
    const { width, height, metadata = {} } = structureResult;

    // Clone grids so passes can mutate freely
    let ground = structureResult.ground.map(row => [...row]);
    let objects = structureResult.objects.map(row => [...row]);
    let overhead = structureResult.overhead
      ? structureResult.overhead.map(row => [...row])
      : this._emptyGrid(width, height);

    const ctx = {
      ground,
      objects,
      overhead,
      width,
      height,
      metadata,
      rng: this.rng,
      options,
    };

    for (const { name, pass, config } of this._passes) {
      const merged = { ...config, ...options[name] };

      switch (name) {
        case 'transition':
          pass.process(ctx.ground, width, height, merged.tileMapping || options.tileMapping);
          break;

        case 'cleanup':
          pass.process(ctx.ground, width, height, {
            ...merged,
            objectGrid: ctx.objects,
          });
          break;

        case 'decoration': {
          const tileLayers = merged.tileLayers || options.tileLayers || null;
          const grids = tileLayers
            ? { ground: ctx.ground, objects: ctx.objects, overhead: ctx.overhead }
            : ctx.objects; // legacy: plain objectGrid
          if (merged.decorations || options.decorations) {
            pass.scatter(
              grids, ctx.ground,
              merged.decorations || options.decorations,
              { width, height, tileLayers }
            );
          }
          if (merged.edgeDecorations) {
            for (const ed of merged.edgeDecorations) {
              pass.placeAlongEdges(grids, ctx.ground, ed.edgeTileId, ed.decorations, tileLayers);
            }
          }
          break;
        }

        case 'autotile':
          // AutotilePass replaces TransitionPass when TileMeta is available
          pass.process(ctx.ground, width, height, () => ctx.rng.next());
          break;

        case 'validation':
          if (merged.floorTileIds || options.floorTileIds) {
            const result = pass.validate(ctx.ground, width, height, {
              floorTileIds: merged.floorTileIds || options.floorTileIds,
              corridorTileId: merged.corridorTileId || options.corridorTileId,
              repair: merged.repair !== undefined ? merged.repair : true,
              ensureExits: merged.ensureExits || false,
            });
            ctx.metadata.validation = result;
          }
          break;

        case 'quality':
          ctx.metadata.quality = pass.score({
            ground: ctx.ground,
            objects: ctx.objects,
            rooms: ctx.metadata.rooms || [],
            width,
            height,
          }, merged);
          break;

        default:
          // Generic pass: call process with context
          if (typeof pass.process === 'function') {
            pass.process(ctx, merged);
          }
          break;
      }
    }

    return {
      ground: ctx.ground,
      objects: ctx.objects,
      overhead: ctx.overhead,
      width,
      height,
      metadata: ctx.metadata,
    };
  }

  _emptyGrid(width, height) {
    const grid = [];
    for (let y = 0; y < height; y++) grid.push(new Array(width).fill(0));
    return grid;
  }

  // ---- factory ----

  /**
   * Create a pipeline with the standard passes.
   *
   * When tileMeta is provided, AutotilePass replaces TransitionPass
   * and QualityScoringPass runs as the final step.
   *
   * @param {import('./SeededRandom.js').SeededRandom} rng
   * @param {object} [options]
   * @param {import('./TileMeta.js').TileMeta} [options.tileMeta] — enables autotile + quality scoring
   * @returns {MapPipeline}
   */
  static createDefault(rng, options = {}) {
    const pipeline = new MapPipeline(rng);

    if (options.tileMeta) {
      pipeline.addPass('autotile', new AutotilePass(options.tileMeta));
    } else {
      pipeline.addPass('transition', new TransitionPass());
    }

    pipeline.addPass('cleanup', new CleanupPass());
    pipeline.addPass('decoration', new DecorationPass(rng));
    pipeline.addPass('validation', new ValidationPass());

    if (options.tileMeta) {
      pipeline.addPass('quality', new QualityScoringPass(options.tileMeta));
    }

    return pipeline;
  }
}
